import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BasicMenuButtonComponent } from '@principle-theorem/ng-editor';
import { PatientDocumentsComponent } from '../patient-documents/patient-documents.component';
import { getMixedContent } from '@principle-theorem/editor';
import {
  IPatient,
  IPatientDocument,
} from '@principle-theorem/principle-core/interfaces';
import { snapshot, WithRef } from '@principle-theorem/shared';
import { type MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface IInsertDocumentMenuData {
  patient: WithRef<IPatient>;
  menuCoordinator$?: Subject<string>;
}

@Component({
  selector: 'pr-insert-patient-document-menu',
  templateUrl: './insert-patient-document-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PatientDocumentsComponent],
  styleUrls: ['./insert-patient-document-menu.component.scss'],
})
export class InsertPatientDocumentMenuComponent
  extends BasicMenuButtonComponent
  implements OnInit
{
  private static readonly _menuId = 'insert-patient-document';

  override tooltip = 'Insert Patient Document';
  override icon = 'folder_shared';
  override buttonText = 'Insert Patient Document';

  @Input() patient: WithRef<IPatient>;
  @Input() menuCoordinator$?: Subject<string>;

  @ViewChild(PatientDocumentsComponent)
  patientDocuments: PatientDocumentsComponent;

  ngOnInit(): void {
    this._subscribeToCoordinator();
  }

  async onDocumentSelected(document: WithRef<IPatientDocument>): Promise<void> {
    const content = getMixedContent(document.content);
    const editor = await snapshot(this.editor$);
    editor.commands.insertContent(content);
  }

  onMenuOpened(): void {
    this.menuCoordinator$?.next(InsertPatientDocumentMenuComponent._menuId);
  }

  private _subscribeToCoordinator(): void {
    if (!this.menuCoordinator$) {
      return;
    }
    this.menuCoordinator$
      .pipe(
        filter((id) => id !== InsertPatientDocumentMenuComponent._menuId),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => this.patientDocuments?.closeMenu());
  }
}

export const createInsertPatientDocumentMenuButton =
  (data: IInsertDocumentMenuData): MenuButtonLoaderFn =>
  () => ({
    component: InsertPatientDocumentMenuComponent,
    data,
  });
